import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react'
import { EmptyState, LoadingScreen, PrimaryButton } from '@/components'
import { DatabaseOperationError } from '@/db/errors'
import { initializeDataLayer, type DataLayerStatus } from '@/db/services/databaseBootstrap'

type BootstrapState =
  | { status: 'loading' }
  | { status: 'ready'; data: DataLayerStatus }
  | { status: 'error'; message: string }

const DataLayerContext = createContext<DataLayerStatus | null>(null)

export function DatabaseBootstrap({ children }: PropsWithChildren) {
  const [state, setState] = useState<BootstrapState>({ status: 'loading' })

  useEffect(() => {
    let active = true
    void initializeDataLayer()
      .then((data) => {
        if (active) setState({ status: 'ready', data })
      })
      .catch((error: unknown) => {
        if (!active) return
        setState({
          status: 'error',
          message:
            error instanceof DatabaseOperationError
              ? error.userMessage
              : 'Não foi possível abrir o armazenamento local com segurança.',
        })
      })
    return () => {
      active = false
    }
  }, [])

  if (state.status === 'loading') {
    return <LoadingScreen message="Preparando o banco local…" />
  }
  if (state.status === 'error') {
    return (
      <EmptyState
        title="Banco local indisponível"
        description={state.message}
        action={
          <PrimaryButton onClick={() => window.location.reload()}>Tentar novamente</PrimaryButton>
        }
      />
    )
  }
  return <DataLayerContext.Provider value={state.data}>{children}</DataLayerContext.Provider>
}

export function useDataLayerStatus() {
  const context = useContext(DataLayerContext)
  if (!context) {
    throw new Error('useDataLayerStatus deve ser usado dentro de DatabaseBootstrap.')
  }
  return context
}
